import React, { useState, useEffect, useCallback } from 'react';
import { getRanking } from '../api';

const API_URL = process.env.REACT_APP_API_URL || 'https://stock-api-production-913a.up.railway.app';
const UPLOAD_TOKEN = 'n0EDu0pwogf6NGKoLrdEA_qE5MfG8kMC';

// [1D_SECTOR_PATCHED]
export default function SectorComparison({ setTicker }) {
  const [watchlist, setWatchlist] = useState([]);

  // [WL_ROLE_PATCHED] 管理員看後端 DB, 訪客看自己的 localStorage
  const loadWatchlist = useCallback(() => {
    const _token = sessionStorage.getItem('wintime_admin_token') || '';
    const isAdmin = !!_token;
    if (isAdmin) {
      fetch(`${API_URL}/api/watchlist`).then(r=>r.json()).then(d=>setWatchlist(Array.isArray(d)?d:[])).catch(()=>{});
    } else {
      try {
        const stored = localStorage.getItem('wt_watchlist');
        setWatchlist(stored ? JSON.parse(stored) : []);
      } catch { setWatchlist([]); }
    }
  }, []);

  useEffect(() => {
    loadWatchlist();
    window.addEventListener('watchlistChanged', loadWatchlist);
    return () => window.removeEventListener('watchlistChanged', loadWatchlist);
  }, [loadWatchlist]);

  const isInWatchlist = t => watchlist.some(w => w.ticker === t);

  const toggleWatchlist = (ticker, name, inWL) => {
    const _token = sessionStorage.getItem('wintime_admin_token') || '';
    const isAdmin = !!_token;
    if (isAdmin) {
      // 管理員: 寫後端 DB
      if (inWL) {
        setWatchlist(prev => prev.filter(w => w.ticker !== ticker));
        fetch(`${API_URL}/api/watchlist/${ticker}?token=${encodeURIComponent(_token)}`, { method:'DELETE' })
          .then(() => window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action:'remove', ticker }})))
          .catch(() => loadWatchlist());
      } else {
        setWatchlist(prev => [...prev, { ticker, name }]);
        fetch(`${API_URL}/api/watchlist/${ticker}?name=${encodeURIComponent(name)}&token=${encodeURIComponent(_token)}`, { method:'POST' })
          .then(r=>r.json()).then(d=>{ if(!d.ok) loadWatchlist(); else window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action:'add', ticker, name }})); })
          .catch(() => loadWatchlist());
      }
    } else {
      // 訪客: 寫 localStorage (與 Sidebar 共用 wt_watchlist key)
      const next = inWL
        ? watchlist.filter(w => w.ticker !== ticker)
        : [...watchlist, { ticker, name }];
      setWatchlist(next);
      try { localStorage.setItem('wt_watchlist', JSON.stringify(next)); } catch {}
      window.dispatchEvent(new CustomEvent('watchlistChanged', { detail:{ action: inWL ? 'remove' : 'add', ticker, name }}));
    }
  };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('rev_yoy');
  const [sortDir, setSortDir] = useState('desc');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterConcept, setFilterConcept] = useState('');
  const [expandedSector, setExpandedSector] = useState(null);
  const [isAdmin, setIsAdmin] = useState(() => !!sessionStorage.getItem('wintime_admin_token'));
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    const onLogin = () => setIsAdmin(true);
    const onLogout = () => setIsAdmin(false);
    window.addEventListener('adminLoggedIn', onLogin);
    window.addEventListener('adminLoggedOut', onLogout);
    return () => {
      window.removeEventListener('adminLoggedIn', onLogin);
      window.removeEventListener('adminLoggedOut', onLogout);
    };
  }, []);

  useEffect(() => {
    if (filterConcept) setExpandedSector(filterConcept);
    else setExpandedSector(null);
  }, [filterConcept]);

  const handleUploadCategories = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadStatus('⏳ 上傳中,請稍候...');
    const formData = new FormData();
    formData.append('file', file);
    fetch(`${API_URL}/api/upload/categories?token=${UPLOAD_TOKEN}`, {
      method: 'POST',
      body: formData,
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setUploadStatus(`✅ 新分類 ${d.updated_nc} 筆、概念股 ${d.updated_cp} 筆已更新`);
          getRanking().then(newD => setData(processData(newD)));
        } else {
          setUploadStatus(`❌ 失敗:${d.error || '未知錯誤'}`);
        }
        setTimeout(() => setUploadStatus(''), 6000);
        e.target.value = '';
      })
      .catch(err => {
        setUploadStatus(`❌ 錯誤:${err.message}`);
        setTimeout(() => setUploadStatus(''), 6000);
      });
  };

  const processData = (rows) => rows.map(s => ({
    ...s,
    op_income_yoy: (s.op_income != null && s.prev_year_op_income != null && Number(s.prev_year_op_income) !== 0)
      ? (Number(s.op_income) - Number(s.prev_year_op_income)) / Math.abs(Number(s.prev_year_op_income)) * 100 : null,
    cur_rev_b: s.cur_rev != null ? (Number(s.cur_rev) / 100000) : null,
    prev_rev_b: s.prev_rev != null ? (Number(s.prev_rev) / 100000) : null,
  }));

  useEffect(() => {
    getRanking().then(d => { setData(processData(d)); setLoading(false); });
  }, []);

  if (loading) return <div className="loading">⏳ 載入類股資料...</div>;

  const allConcepts = [...new Set(data.filter(r => r.concept).map(r => r.concept))].sort();
  const filteredData = filterConcept ? data.filter(r => r.concept === filterConcept) : data;

  // 分組
  const industries = {};
  if (filterConcept) {
    industries[filterConcept] = filteredData;
  } else {
    filteredData.forEach(row => {
      const key = row.new_category || row.sub_industry || row.industry || '其他';
      if (!industries[key]) industries[key] = [];
      industries[key].push(row);
    });
  }

  // 類股合計
  const sumNum = (arr, f) => arr.filter(s => s[f]!=null && s[f]!=='' && !isNaN(Number(s[f])))
                                .reduce((a,b)=>a+Number(b[f]),0);
  const hasVal = (arr, f) => arr.some(s => s[f]!=null && s[f]!=='' && !isNaN(Number(s[f])));
  const wAvg = (arr, valField, weightField) => {
    const valid = arr.filter(s => s[valField]!=null && s[valField]!=='' && !isNaN(Number(s[valField])) &&
                                  s[weightField]!=null && Number(s[weightField])>0);
    if (!valid.length) return null;
    const wsum = valid.reduce((a,s)=>a+Number(s[weightField]),0);
    if (!wsum) return null;
    return valid.reduce((a,s)=>a+Number(s[valField])*Number(s[weightField]),0) / wsum;
  };
  const avgN = (arr, f) => {
    const v = arr.filter(s=>s[f]!=null && !isNaN(Number(s[f])));
    return v.length ? v.reduce((a,s)=>a+Number(s[f]),0)/v.length : null;
  };
  /* SECTOR_PCT_V1 */ const pctChg = (now, prev) => {  // (本週-上週)/上週*100
    const p = Number(prev);
    if (!p || isNaN(p) || now==null || now==='' || isNaN(Number(now))) return null;
    return (Number(now) - p) / Math.abs(p) * 100;
  };

  const calcSector = (stocks) => {
    const cur_rev_sum = sumNum(stocks, 'cur_rev');
    const prev_rev_sum = sumNum(stocks, 'prev_rev');
    const rev_yoy = (hasVal(stocks,'cur_rev') && prev_rev_sum!==0)
      ? (cur_rev_sum-prev_rev_sum)/Math.abs(prev_rev_sum)*100 : null;

    const op_sum = sumNum(stocks, 'op_income');
    const py_op_sum = sumNum(stocks, 'prev_year_op_income');
    const op_income_yoy = (hasVal(stocks,'op_income') && hasVal(stocks,'prev_year_op_income') && py_op_sum!==0)
      ? (op_sum-py_op_sum)/Math.abs(py_op_sum)*100 : null;

    return {
      price_change_1d_avg: avgN(stocks, 'price_change_1d'),
      rev_yoy,
      cur_rev_b: cur_rev_sum > 0 ? cur_rev_sum / 100000 : null,
      prev_rev_b: prev_rev_sum > 0 ? prev_rev_sum / 100000 : null,
      gross_rate: wAvg(stocks, 'gross_rate', 'rev'),
      gross_rate_chg: wAvg(stocks, 'gross_rate_chg', 'rev'),
      op_rate: wAvg(stocks, 'op_rate', 'rev'),
      op_rate_chg: wAvg(stocks, 'op_rate_chg', 'rev'),
      op_income_yoy,
      big_shares_pct: pctChg(sumNum(stocks,'big_shares_now'), sumNum(stocks,'big_shares_prev')),
      retail_holders_pct: pctChg(sumNum(stocks,'retail_holders_now'), sumNum(stocks,'retail_holders_prev')),
    };
  };

  let sectorRows = Object.entries(industries).map(([name, stocks]) => ({
    name, count: stocks.length, stocks, ...calcSector(stocks)
  }));

  if (filterIndustry.trim()) {
    sectorRows = sectorRows.filter(r => r.name.includes(filterIndustry.trim()));
  }

  sectorRows.sort((a, b) => {
    const va = a[sortField] ?? -Infinity;
    const vb = b[sortField] ?? -Infinity;
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  const fmt = (v, dec=2) => v == null || isNaN(v) ? '-' : Number(v).toFixed(dec) + '%';
  const fmtChg = v => v == null || isNaN(v) ? '-' : (v > 0 ? '+' : '') + Number(v).toFixed(2);
  const fmtN = v => v == null || isNaN(v) ? '-' : (v > 0 ? '+' : '') + Number(v).toLocaleString();
  const fmtPct = v => v == null || isNaN(v) ? '-' : (v > 0 ? '+' : '') + Number(v).toFixed(2) + '%';
  const fmtB = v => v == null || isNaN(v) ? '-' : Number(v).toFixed(2);
  const colorVal = v => v == null || isNaN(v) ? '#aaa' : v > 0 ? '#d62828' : v < 0 ? '#00a14b' : '#aaa';
  const colorValInv = v => v == null || isNaN(v) ? '#aaa' : v > 0 ? '#00a14b' : v < 0 ? '#d62828' : '#aaa';  /* 散戶反指標:增加綠/減少紅 */

  // [5D_SORT_PATCHED]
  const SORT_FIELDS = [
    { key: 'rev_yoy', label: '月營收年增率' },
    { key: 'gross_rate_chg', label: '毛利率季增' },
    { key: 'op_rate_chg', label: '營益率季增' },
    { key: 'op_income_yoy', label: '營業利益年增率' },
    { key: 'price_change_1d_avg', label: '當天漲跌' },
  ];

  return (
    <div style={{ padding: '8px' }}>
      <h2 style={{ marginBottom: 16, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        🏭 全市場類股排名
        {isAdmin && (
          <>
            <label style={{
              padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
              background: '#4C9BB8', color: '#fff', fontSize: 13, fontWeight: 600,
              border: 'none', display: 'inline-block'
            }}>
              📁 更新分類/概念股
              <input type="file" accept=".xlsx,.xls" onChange={handleUploadCategories} style={{ display: 'none' }} />
            </label>
            {uploadStatus && (
              <span style={{
                fontSize: 13,
                color: uploadStatus.startsWith('✅') ? '#22cc66'
                     : uploadStatus.startsWith('❌') ? '#ff5555' : '#f5c518'
              }}>
                {uploadStatus}
              </span>
            )}
          </>
        )}
      </h2>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="篩選分類（可留空）"
          value={filterIndustry}
          onChange={e => setFilterIndustry(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #333', background: '#1e2a3a', color: '#fff', width: 200 }}
        />
        <span style={{ color: '#ccc', fontSize: 13 }}>共 {sectorRows.length} 個分類</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {SORT_FIELDS.map(f => (
          <button key={f.key}
            onClick={() => { if (sortField === f.key) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortField(f.key); setSortDir('desc'); } }}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: sortField === f.key ? '#4C9BB8' : '#2a3a4a', color: '#fff', fontSize: 13, fontWeight: sortField === f.key ? 700 : 400 }}>
            {f.label} {sortField === f.key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 12, display: 'flex', gap: 16 }}>
        {['desc', 'asc'].map(d => (
          <label key={d} style={{ cursor: 'pointer', color: sortDir === d ? '#4C9BB8' : '#aaa', fontSize: 14 }}>
            <input type="radio" checked={sortDir === d} onChange={() => setSortDir(d)} style={{ marginRight: 4 }} />
            {d === 'desc' ? '由高到低' : '由低到高'}
          </label>
        ))}
      </div>

      {allConcepts.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
                      padding: '10px 12px', background: '#131c2a', borderRadius: 6, border: '1px solid #2a3a4a' }}>
          <span style={{ color: '#f5c518', fontSize: 13, fontWeight: 700 }}>📌 概念股</span>
          <select value={filterConcept} onChange={e => setFilterConcept(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 4,
              background: filterConcept ? '#f5c518' : '#2a3a4a',
              color: filterConcept ? '#000' : '#fff',
              border: '1px solid #2a3a4a', fontSize: 13,
              fontWeight: filterConcept ? 700 : 400, cursor: 'pointer', minWidth: 200 }}>
            <option value="">全部（不篩選）</option>
            {allConcepts.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {filterConcept && (
            <>
              <button onClick={() => setFilterConcept('')}
                style={{ padding: '5px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                         background: '#2a3a4a', color: '#fff', fontSize: 12 }}>✕ 清除</button>
              <span style={{ color: '#aaa', fontSize: 12 }}>目前篩選:{filteredData.length} 檔</span>
            </>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0d1520', color: '#ddd' }}>
              <th style={{ ...th, position:'sticky', left:0, zIndex:20, background:'#0d1520', minWidth:140, textAlign:'left' }}>分類／代號名稱</th>
              <th style={th}>家數</th>
              <th style={th}>收盤價</th>
              <th style={th}>當天漲跌(%)</th>
              <th style={th}>大戶持股增減(%)</th>
              <th style={th}>散戶人數增減(%)</th>
              <th style={th}>月營收年增率(%)</th>
              <th style={th}>本月營收(億)</th>
              <th style={th}>去年同月(億)</th>
              <th style={th}>本季毛利率(%)</th>
              <th style={th}>毛利率增減(pp)</th>
              <th style={th}>本季營益率(%)</th>
              <th style={th}>營益率增減(pp)</th>
              <th style={th}>營業利益年增率(%)</th>
            </tr>
          </thead>
          <tbody>
            {sectorRows.map((row, i) => {
              const rowBg = i % 2 === 0 ? '#131c2a' : '#182435';
              return (
                <React.Fragment key={row.name}>
                  <tr onClick={() => setExpandedSector(expandedSector === row.name ? null : row.name)}
                    style={{ background: rowBg, cursor: 'pointer' }}>
                    <td style={{ ...td, color:'#f5c518', textAlign:'left', position:'sticky', left:0, background:rowBg, fontWeight:700 }}>
                      <span style={{ marginRight: 8, color:'#4C9BB8', fontSize: 11 }}>
                        {expandedSector === row.name ? '▼' : '▶'}
                      </span>
                      {row.name} <span style={{ color:'#aaa', fontWeight:400 }}>({row.count})</span>
                    </td>
                    <td style={{ ...td, color:'#f5c518', textAlign:'center' }}>{row.count}</td>
                    <td style={{ ...td, color:'#888' }}>—</td>
                    <td style={{ ...td, color: colorVal(row.price_change_1d_avg) }}>
                      {row.price_change_1d_avg != null ? (row.price_change_1d_avg > 0 ? '+' : '') + row.price_change_1d_avg.toFixed(2) + '%' : '-'}
                    </td>
                    <td style={{ ...td, color: colorVal(row.big_shares_pct) }}>{fmtPct(row.big_shares_pct)}</td>
                    <td style={{ ...td, color: colorValInv(row.retail_holders_pct) }}>{fmtPct(row.retail_holders_pct)}</td>
                    <td style={{ ...td, color: colorVal(row.rev_yoy) }}>{fmt(row.rev_yoy)}</td>
                    <td style={{ ...td, color:'#ddd' }}>{fmtB(row.cur_rev_b)}</td>
                    <td style={{ ...td, color:'#ddd' }}>{fmtB(row.prev_rev_b)}</td>
                    <td style={{ ...td, color:'#ccc' }}>{fmt(row.gross_rate)}</td>
                    <td style={{ ...td, color: colorVal(row.gross_rate_chg) }}>{fmtChg(row.gross_rate_chg)}</td>
                    <td style={{ ...td, color:'#ccc' }}>{fmt(row.op_rate)}</td>
                    <td style={{ ...td, color: colorVal(row.op_rate_chg) }}>{fmtChg(row.op_rate_chg)}</td>
                    <td style={{ ...td, color: colorVal(row.op_income_yoy) }}>{fmt(row.op_income_yoy)}</td>
                  </tr>
                  {expandedSector === row.name && row.stocks.map((s, j) => {
                    const sBg = j % 2 === 0 ? '#0e1620' : '#101a26';
                    const inWL = isInWatchlist(s.ticker);
                    return (
                      <tr key={s.ticker} style={{ background: sBg }}>
                        <td style={{ ...td, paddingLeft: 30, color:'#ddd', textAlign:'left', position:'sticky', left:0, background:sBg }}>
                          <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                            <input type="checkbox" checked={inWL}
                              onChange={e => { e.stopPropagation(); toggleWatchlist(s.ticker, s.name, inWL); }}
                              style={{ cursor:'pointer', width:14, height:14 }} />
                            <span style={{ color:'#4C9BB8', cursor:'pointer', fontWeight:700 }}
                              onClick={() => setTicker && setTicker(s.ticker)}>{s.ticker}</span>
                            <span style={{ color:'#aaa', fontSize:11 }}>{s.name}</span>
                          </span>
                        </td>
                        <td style={{ ...td, color:'#888' }}>—</td>
                        <td style={{ ...td, color:'#fff', fontFamily:"'JetBrains Mono',monospace" }}>
                          {s.close_now ? Number(s.close_now).toLocaleString() : '-'}
                        </td>
                        <td style={{ ...td, color: colorVal(s.price_change_1d) }}>
                          {s.price_change_1d != null ? (s.price_change_1d > 0 ? '+' : '') + s.price_change_1d + '%' : '-'}
                        </td>
                        <td style={{ ...td, color: colorVal(pctChg(s.big_shares_now, s.big_shares_prev)) }}>{fmtPct(pctChg(s.big_shares_now, s.big_shares_prev))}</td>
                        <td style={{ ...td, color: colorValInv(pctChg(s.retail_holders_now, s.retail_holders_prev)) }}>{fmtPct(pctChg(s.retail_holders_now, s.retail_holders_prev))}</td>
                        <td style={{ ...td, color: colorVal(s.rev_yoy) }}>{fmt(s.rev_yoy)}</td>
                        <td style={{ ...td, color:'#ddd' }}>{s.cur_rev_b != null ? s.cur_rev_b.toFixed(2) : '-'}</td>
                        <td style={{ ...td, color:'#ddd' }}>{s.prev_rev_b != null ? s.prev_rev_b.toFixed(2) : '-'}</td>
                        <td style={{ ...td, color:'#ccc' }}>{fmt(s.gross_rate)}</td>
                        <td style={{ ...td, color: colorVal(s.gross_rate_chg) }}>{fmtChg(s.gross_rate_chg)}</td>
                        <td style={{ ...td, color:'#ccc' }}>{fmt(s.op_rate)}</td>
                        <td style={{ ...td, color: colorVal(s.op_rate_chg) }}>{fmtChg(s.op_rate_chg)}</td>
                        <td style={{ ...td, color: colorVal(s.op_income_yoy) }}>{fmt(s.op_income_yoy)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #2a3a4a', position: 'sticky', top: 0, background: '#0d1520', zIndex: 10, fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #1e2a3a', fontSize: 12, whiteSpace: 'nowrap' };
